import { useContext, useEffect, useState } from 'react';

import { OptimizelyContext, OptimizelyDecideOption, useDecision } from '@optimizely/react-sdk';

import { EXPERIMENT_RULE_KEYS, FLAG } from 'Utilities/analytics/optimizely/constants';
import useGetVehicleEnquiry from 'Utilities/vehicles/hooks/useGetVehicleEnquiry';

import { OfferPropTypes } from '../../acceptOffer/AcceptOffer.types';

import { EXPERIMENT_VARIATIONS } from './DealerRatingsExperiment.consts';
import { getIsDealerInCohort, getIsReservePriceMet } from './DealerRatingsExperiment.helpers';

export type UseDealerRatingsExperimentProps = {
  dpEnquiryId?: number;
  offer?: OfferPropTypes | null;
};

export const useDealerRatingsExperiment = ({ dpEnquiryId, offer }: UseDealerRatingsExperimentProps) => {
  const { optimizely } = useContext(OptimizelyContext);
  const [isReadyToTrackMeasuringDecision, setIsReadyToTrackMeasuringDecision] = useState(false);

  const { TEST } = EXPERIMENT_VARIATIONS;
  const { closingBid } = offer ?? {};
  const { dealerContact: { dealer: { id: dealerId } = {} } = {}, value: closingBidValue } = closingBid ?? {};

  const enquiryId = String(dpEnquiryId);
  const hasValidIds = Boolean(enquiryId && dealerId);
  const isDealerInCohort = getIsDealerInCohort(dealerId);
  const isEligibleForExperiment = hasValidIds && isDealerInCohort;

  const { enquiry: { prepCosts, priceHigh, sellerWillAcceptPrice } = {} } = useGetVehicleEnquiry({
    dpEnquiryId,
  });

  const isReservePriceMet = getIsReservePriceMet({
    closingBidValue,
    prepCosts,
    priceHigh,
    sellerWillAcceptPrice,
  });

  const decisionAttributes = {
    overrideAttributes: {
      reserve_met: isReservePriceMet,
      winning_dealer_id: dealerId ?? 0,
    },
    overrideUserId: enquiryId,
  };

  const [{ enabled: isConsistencyEnabled, variationKey: consistencyDecision }] = useDecision(
    FLAG.DEALER_RATINGS_CONSISTENCY_EXPERIMENT,
    { decideOptions: isEligibleForExperiment ? [] : [OptimizelyDecideOption.DISABLE_DECISION_EVENT] },
    {
      ...decisionAttributes,
    },
  );

  const shouldTrackMeasuringDecision = Boolean(isEligibleForExperiment && isConsistencyEnabled && consistencyDecision);

  useEffect(() => {
    if (consistencyDecision && shouldTrackMeasuringDecision && optimizely) {
      optimizely.onReady().then(() => {
        optimizely.setForcedVariation(
          EXPERIMENT_RULE_KEYS.DEALER_RATINGS_MEASURING_EXPERIMENT.AA_TEST_ROLLOUT,
          enquiryId,
          consistencyDecision,
        );

        setIsReadyToTrackMeasuringDecision(true);
      });
    }
  }, [consistencyDecision, optimizely, shouldTrackMeasuringDecision, enquiryId]);

  const [{ variationKey: measuringDecision }] = useDecision(
    FLAG.DEALER_RATINGS_MEASURING_EXPERIMENT,
    { decideOptions: isReadyToTrackMeasuringDecision ? [] : [OptimizelyDecideOption.DISABLE_DECISION_EVENT] },
    {
      ...decisionAttributes,
    },
  );

  const isConsistencyAndMeasuringDecisionMismatch = consistencyDecision !== measuringDecision;

  useEffect(() => {
    if (isReadyToTrackMeasuringDecision && shouldTrackMeasuringDecision && isConsistencyAndMeasuringDecisionMismatch) {
      const message = 'Mismatch between the dealer ratings consistency and measuring experiments, check the Optimizely flag setup.';

      globalThis?.Sentry?.captureException(new Error(message), {
        level: 'warning',
      });
    }
  }, [isReadyToTrackMeasuringDecision, shouldTrackMeasuringDecision, isConsistencyAndMeasuringDecisionMismatch]);

  if (!isEligibleForExperiment || isConsistencyAndMeasuringDecisionMismatch) {
    return false;
  }

  return consistencyDecision === TEST;
};
